import { AfterViewInit, Component } from '@angular/core';
import { ContactDto } from '@models/users/contact.dto';
import { OpenCloseType } from '@models/users/open-close-type.enum';
import { PeriodDto } from '@models/users/period.dto';
import { RequestTopic } from '@models/users/request-topic.dto';
import { UserRequest } from '@models/users/user-request.dto';
import { ContactService } from '@services/contact.service';
import { MessageService } from '@services/message.service';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { catchError } from 'rxjs';
import { SequenceCreatorDialogComponent } from './sequence-creator-dialog/sequence-creator-dialog.component';
import { SequenceValidatorDialogComponent } from './sequence-validator-dialog/sequence-validator-dialog.component';

@Component({
  selector: 'ebs-contacts',
  templateUrl: './contacts.component.html',
  styleUrls: ['./contacts.component.scss'],
})
export class ContactsComponent implements AfterViewInit {
  contacts: ContactDto[] = [];

  periods: PeriodDto[] = [];
  editingPeriods: { [key: number]: boolean } = {};

  requests: UserRequest[] = [];
  clonedContactDtos: { [s: number]: ContactDto } = {};
  creating: ContactDto | undefined;
  showTopic: boolean = false;
  public OpenCloseType: typeof OpenCloseType = OpenCloseType;

  selectedReport: UserRequest | null = null;

  public currentlyEditing: RequestTopic | null = null;
  public topics: RequestTopic[] = [];


  constructor(
    private contactService: ContactService,
    public dialogService: DialogService,
    private messageService: MessageService,
  ) {}
  ngAfterViewInit(): void {
    this.loadAll();
  }

  loadAll() {
    this.loadContacts();
    this.loadTopics();
    this.loadRequests();
    this.loadPeriods();
  }

  loadContacts() {
    this.contactService.getContacts().subscribe((res) => {
      //this.contacts = res;
      this.contacts = [...res].sort((a, b) => a.position - b.position);
    });
  }

  loadRequests() {
    this.contactService.getRequestList().subscribe((res) => {
      this.requests = res;
    });
  }

  loadPeriods() {
    this.contactService.getAvailabilityView().subscribe((res) => {
      this.periods = res;
    });
  }

  loadTopics() {
    this.contactService.getTopicList().subscribe((res) => {
      this.topics = res;
    });
  }

  onRowEditInit(contact: ContactDto) {
    this.clonedContactDtos[contact.contactId as number] = { ...contact };
  }

  createNewContact() {
    this.creating = {
      icon: 'phone',
      contact: '',
      description: '',
      position: this.contacts.length + 1,
    };
  }

  showTopicDialog() {
    this.showTopic = true;
  }

  confirmCreate() {
    if (this.creating !== undefined) {
      this.contactService.createContact([this.creating]).subscribe((result) => {
        this.creating = undefined;
        this.loadContacts();
      });
    }
  }

  onRowEditSave(contact: ContactDto) {
    this.contactService.modifyContact([contact]).subscribe((res) => {
      delete this.clonedContactDtos[contact.contactId as number];
      this.loadContacts();
    });
  }

  removeContact(contact: ContactDto) {
    this.contactService.removeContact([contact]).subscribe((res) => {
      this.loadContacts();
    });
  }

  onRowEditCancel(contact: ContactDto, index: number) {
    this.contacts[index] = this.clonedContactDtos[contact.contactId as number];
    delete this.clonedContactDtos[contact.contactId as number];
  }

  createNewTopic() {
    this.topics.splice(0, 0, {
      bccRecipient: '',
      ccRecipient: '',
      toRecipient: '',
      description: '',
      subject: '',
    });

    this.currentlyEditing = this.topics[0];
  }

  removeTopic(topic: RequestTopic) {
    this.contactService
      .removeTopic([topic])
      .pipe(
        catchError((err) => {
          this.currentlyEditing = null;
          this.messageService.error(
            'Rimozione argomento',
            'Errore durante la rimozione',
          );
          return err;
        }),
      )
      .subscribe((res) => {
        this.currentlyEditing = null;
        this.loadAll();
        this.messageService.success(
          'Rimozione argomento',
          'Rimozione avvenuta con successo',
        );
      });
  }

  editTopic(topic: RequestTopic | null) {
    if (
      topic === null &&
      (this.currentlyEditing?.topicId === undefined ||
        this.currentlyEditing?.topicId === null)
    ) {
      this.topics.splice(0, 1);
    }
    this.currentlyEditing = topic;
  }

  modifyTopic() {
    if (this.currentlyEditing !== null) {
      this.saveTopicChanges(this.currentlyEditing);
    }
  }

  saveTopicChanges(topic: RequestTopic) {
    this.contactService
      .updateTopic([topic])
      .pipe(
        catchError((err) => {
          this.currentlyEditing = null;
          this.messageService.error(
            'Modifica argomento',
            'Errore durante la modifica',
          );
          return err;
        }),
      )
      .subscribe((res) => {
        this.currentlyEditing = null;
        this.loadAll();
        this.messageService.success(
          'Modifica argomento',
          'Modifica avvenuta con successo',
        );
      });
  }

  showReport(request: UserRequest) {
    this.selectedReport = request;
  }

  hideReport() {
    this.selectedReport = null;
  }

  removeRequest(request: UserRequest) {
    this.contactService
      .removeRequest(request)
      .pipe(
        catchError((err) => {
          this.currentlyEditing = null;
          this.messageService.error(
            'Rimozione richiesta',
            'Errore durante la rimozione',
          );
          return err;
        }),
      )
      .subscribe((res) => {
        this.currentlyEditing = null;
        this.loadAll();
        this.messageService.success(
          'Rimozione richiesta',
          'Rimozione avvenuta con successo',
        );
      });
  }

  closeRequest(request: UserRequest) {
    request.status = OpenCloseType.CLOSE;
    this.contactService
      .closeRequest(request)
      .pipe(
        catchError((err) => {
          this.currentlyEditing = null;
          this.messageService.error(
            'Chiusura richiesta',
            'Errore durante la chiusura',
          );
          return err;
        }),
      )
      .subscribe((res) => {
        this.currentlyEditing = null;
        this.loadAll();
        this.messageService.success(
          'Chiusura richiesta',
          'Chiusura avvenuta con successo',
        );
      });
  }

  startGeneratingSequence() {
    let dialogRef: DynamicDialogRef = this.dialogService.open(
      SequenceCreatorDialogComponent,
      {
        header: 'Generazione automatica reperibilità',
        maximizable: false,
        draggable: false,
        closable: true,
        closeOnEscape: false,
      },
    );

    dialogRef.onClose.subscribe((result: PeriodDto[]) => {
      if (result) {
        this.startValidatingSequence(result);
      }
    });
  }

  startValidatingSequence(periods: PeriodDto[]) {
    let dialogRef: DynamicDialogRef = this.dialogService.open(
      SequenceValidatorDialogComponent,
      {
        maximizable: false,
        draggable: false,
        closable: true,
        closeOnEscape: false,
        width: '100%',
        data: {
          periods,
        },
      },
    );

    dialogRef.onClose.subscribe((result: PeriodDto[]) => {
      if (result) {
        this.createPeriod(result);
      }
    });
  }

  removePeriod(period: PeriodDto) {
    this.contactService
      .removeAvailability([period])
      .pipe(
        catchError((err) => {
          this.currentlyEditing = null;
          this.messageService.error(
            'Rimozione periodo',
            'Errore durante la rimozione',
          );
          return err;
        }),
      )
      .subscribe((res) => {
        this.currentlyEditing = null;
        this.loadAll();
        this.messageService.success(
          'Rimozione periodo',
          'Rimozione avvenuta con successo',
        );
      });
  }

  createPeriod(periods: PeriodDto[]) {
    this.contactService
      .createAvailability(periods)
      .pipe(
        catchError((err) => {
          this.currentlyEditing = null;
          this.messageService.error(
            'Creazione turni',
            'Errore durante la creazione',
          );
          return err;
        }),
      )
      .subscribe((res) => {
        this.currentlyEditing = null;
        this.loadAll();
        this.messageService.success(
          'Creazione turni',
          'Creazione avvenuta con successo',
        );
      });
  }

  modifyPeriod(period: PeriodDto) {
    this.contactService
      .modifyAvailability([period])
      .pipe(
        catchError((err) => {
          this.currentlyEditing = null;
          this.messageService.error(
            'Modifica turni',
            'Errore durante la modifica',
          );
          return err;
        }),
      )
      .subscribe((res) => {
        this.currentlyEditing = null;
        this.loadAll();
        this.messageService.success(
          'Modifica turni',
          'Modifica avvenuta con successo',
        );
      });
  }
}
